import React, {useState, useRef, useEffect} from 'react';

//@ts-ignore
import {Play, Pause} from 'lucide-react';

// onUnplayable: called when the browser can't play the file (e.g. Ogg/Opus in older Safari), so the page can explain it
export const AudioWaveform = ({
                                  audioSrc = "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3",
                                  setSeconds,
                                  onUnplayable
                              }: any) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [bars, setBars] = useState<number[]>([]);
    const audioRef = useRef<HTMLAudioElement>(null);

    // Generate random waveform bars (in a real app, you'd analyze the audio file)
    useEffect(() => {
        const generateBars = () => {
            const numBars = 50;
            const newBars = Array.from({length: numBars}, () =>
                Math.random() * 0.5 + 0.3
            );
            setBars(newBars);
        };
        generateBars();
    }, [audioSrc]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handleLoadedMetadata = () => {
            setDuration(audio.duration);
            if (setSeconds) {
                setSeconds(audio.duration)
            }
        };

        const handleTimeUpdate = () => {
            setCurrentTime(audio.currentTime);
        };

        const handleEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
        };

        // Fires while loading when the format isn't supported
        const handleError = () => {
            setIsPlaying(false);
            onUnplayable?.();
        };

        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('error', handleError);

        return () => {
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('error', handleError);
        };
    }, [setSeconds, onUnplayable]);

    const togglePlayPause = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
            setIsPlaying(false);
            return;
        }
        // Only show "playing" once it really plays. AbortError just means a pause interrupted it.
        audio.play()
            .then(() => setIsPlaying(true))
            .catch((err) => {
                setIsPlaying(false);
                if (err?.name !== 'AbortError') onUnplayable?.();
            });
    };

    const handleWaveformClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const audio = audioRef.current;
        if (!audio || !duration) return;

        const bounds = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - bounds.left;
        const percentage = x / bounds.width;
        const newTime = percentage * duration;

        audio.currentTime = newTime;
        setCurrentTime(newTime);
    };

    const formatTime = (time: number) => {
        const mins = Math.floor(time / 60);
        const secs = Math.floor(time % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

    const styles = {
        container: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '5px',
            backgroundColor: 'transparent',
            borderRadius: '16px',
            maxWidth: '100%',
        } as React.CSSProperties,
        playButton: {
            flexShrink: 0,
            width: '38px',
            height: '38px',
            borderRadius: '50%',
            backgroundColor: 'var(--blue)',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
        } as React.CSSProperties,
        playButtonHover: {
            backgroundColor: 'var(--blue)',
        } as React.CSSProperties,
        waveformContainer: {
            flex: 1,
            // Lets the waveform shrink inside narrow (phone-width) bubbles instead of overflowing them
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
        } as React.CSSProperties,
        waveform: {
            position: 'relative',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            overflow: 'hidden',
            cursor: 'pointer',
        } as React.CSSProperties,
        bar: {
            flex: 1,
            borderRadius: '9999px',
            transition: 'all 0.15s',
            minWidth: '1px',
        } as React.CSSProperties,
        timeContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '12px',
            color: '#6b7280',
        } as React.CSSProperties,
        appContainer: {
            minHeight: '100vh',
            background: 'linear-gradient(to bottom right, #e9d5ff, #dbeafe)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
        } as React.CSSProperties,
        contentWrapper: {
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
        } as React.CSSProperties,
        title: {
            fontSize: '30px',
            fontWeight: 'bold',
            color: '#1f2937',
            textAlign: 'center',
            marginBottom: '32px',
        } as React.CSSProperties,
        description: {
            fontSize: '14px',
            color: '#4b5563',
            textAlign: 'center',
            maxWidth: '448px',
        } as React.CSSProperties,
    };

    const [isHovering, setIsHovering] = useState(false);

    return (
        <div style={styles.container}>
            <audio ref={audioRef} src={audioSrc} preload="metadata"/>

            <button
                onClick={togglePlayPause}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                style={{
                    ...styles.playButton,
                    ...(isHovering ? styles.playButtonHover : {}),
                }}
            >
                {isPlaying ? (
                    <Pause size={20} fill="white"/>
                ) : (
                    <Play size={20} fill="white" style={{marginLeft: '2px'}}/>
                )}
            </button>

            <div style={styles.waveformContainer}>
                <div
                    style={styles.waveform}
                    onClick={handleWaveformClick}
                >
                    {bars.map((height, i) => {
                        const barProgress = (i / bars.length) * 100;
                        const isActive = barProgress <= progress;

                        return (
                            <div
                                key={i}
                                style={{
                                    ...styles.bar,
                                    height: `${height * 100}%`,
                                    backgroundColor: isActive ? 'var(--blue)' : '#d1d5db',
                                }}
                            />
                        );
                    })}
                </div>

                <div style={styles.timeContainer}>
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                </div>
            </div>
        </div>
    );
};
